import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Input, Space, Popconfirm, message, Tag, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import tagStore from '../../store/TagStore';
import { useBitrix } from '../../context/BitrixContext';

// Предопределенная палитра цветов
const COLOR_PALETTE = [
    '#f5222d', // red
    '#fa541c', // volcano
    '#fa8c16', // orange
    '#faad14', // gold
    '#fadb14', // yellow
    '#a0d911', // lime
    '#52c41a', // green
    '#13c2c2', // cyan
    '#1677ff', // blue
    '#2f54eb', // geekblue
    '#722ed1', // purple
    '#eb2f96', // magenta
    '#ff4d4f', // red-5
    '#ff7a45', // volcano-5
    '#ffa940', // orange-5
    '#ffc53d', // gold-5
    '#bae637', // lime-5
    '#73d13d', // green-5
    '#36cfc9', // cyan-5
    '#40a9ff', // blue-5
    '#597ef7', // geekblue-5
    '#9254de', // purple-5
    '#f759ab', // magenta-5
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TagManagementModal = observer(({ open, onClose }) => {
    const { user, auth } = useBitrix();
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [editingColor, setEditingColor] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newColor, setNewColor] = useState(COLOR_PALETTE[0]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [canEditTags, setCanEditTags] = useState(false);

    useEffect(() => {
        if (!open) return;
        tagStore.fetchTags();
        setSelectedRowKeys([]);
        setIsCreating(false);
        setEditingId(null);
    }, [open]);

    useEffect(() => {
        if (!open || !user || !auth) return;
        let isMounted = true;
        fetch(`${API_URL}/permissions/me`, {
            headers: {
                'X-Bitrix-User': user.ID,
                'X-Bitrix-Auth': auth,
            },
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (!isMounted) return;
                setCanEditTags(!!data?.canEditTags || !!data?.isAdmin);
            })
            .catch(() => {
                if (!isMounted) return;
                setCanEditTags(false);
            });

        return () => {
            isMounted = false;
        };
    }, [open, user, auth]);

    const handleStartEdit = (tag) => {
        setEditingId(tag.id);
        setEditingName(tag.name);
        setEditingColor(tag.color);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingName('');
        setEditingColor('');
    };

    const handleSaveEdit = async () => {
        if (!canEditTags) {
            message.error('Нет прав на изменение тегов');
            return;
        }
        if (!editingName.trim()) {
            message.error('Название тега не может быть пустым');
            return;
        }
        
        const normalizedName = editingName.trim().slice(0, 10);
        await tagStore.updateTag(editingId, {
            name: normalizedName,
            color: editingColor
        });
        
        setEditingId(null);
        setEditingName('');
        setEditingColor('');
    };

    const handleDelete = async (id) => {
        if (!canEditTags) {
            message.error('Нет прав на изменение тегов');
            return;
        }
        await tagStore.deleteTag(id);
    };

    const handleBulkDelete = async () => {
        if (!canEditTags) {
            message.error('Нет прав на изменение тегов');
            return;
        }
        if (selectedRowKeys.length === 0) {
            message.warning('Выберите теги для удаления');
            return;
        }

        for (const id of selectedRowKeys) {
            await tagStore.deleteTag(id);
        }
        
        setSelectedRowKeys([]);
        message.success(`Удалено тегов: ${selectedRowKeys.length}`);
    };

    const handleCreateTag = async () => {
        if (!canEditTags) {
            message.error('Нет прав на изменение тегов');
            return;
        }
        if (!newName.trim()) {
            message.error('Название тега не может быть пустым');
            return;
        }
        const normalizedName = newName.trim().slice(0, 10);
        await tagStore.createTag({
            name: normalizedName,
            color: newColor
        });

        setIsCreating(false);
        setNewName('');
        setNewColor(COLOR_PALETTE[0]);
    };

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => {
                if (editingId === record.id) {
                    return (
                        <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onPressEnter={handleSaveEdit}
                            autoFocus
                            maxLength={10}
                            showCount
                        />
                    );
                }
                return <Tag color={record.color}>{text}</Tag>;
            },
        },
        {
            title: 'Цвет',
            dataIndex: 'color',
            key: 'color',
            width: 300,
            render: (color, record) => {
                if (editingId === record.id) {
                    return (
                        <Space wrap size={[4, 4]}>
                            {COLOR_PALETTE.map((c) => (
                                <div
                                    key={c}
                                    onClick={() => setEditingColor(c)}
                                    style={{
                                        width: 24,
                                        height: 24,
                                        backgroundColor: c,
                                        cursor: 'pointer',
                                        border: editingColor === c ? '2px solid #000' : '1px solid #d9d9d9',
                                        borderRadius: 4,
                                    }}
                                />
                            ))}
                        </Space>
                    );
                }
                return (
                    <div
                        style={{
                            width: 24,
                            height: 24,
                            backgroundColor: color,
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                        }}
                    />
                );
            },
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 120,
            render: (_, record) => {
                if (editingId === record.id) {
                    return (
                        <Space>
                            <Button type="link" size="small" onClick={handleSaveEdit}>
                                Сохранить
                            </Button>
                            <Button type="link" size="small" onClick={handleCancelEdit}>
                                Отмена
                            </Button>
                        </Space>
                    );
                }
                return (
                    <Space>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => handleStartEdit(record)}
                            disabled={!canEditTags}
                        >
                            Изменить
                        </Button>
                        <Popconfirm
                            title="Удалить тег?"
                            description="Это действие нельзя отменить"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Да"
                            cancelText="Нет"
                            disabled={!canEditTags}
                        >
                            <Button type="link" size="small" danger icon={<DeleteOutlined />} disabled={!canEditTags} />
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <Modal
            title="Управление тегами"
            open={open}
            onCancel={onClose}
            width={700}
            zIndex={1100}
            getContainer={() => document.body}
            bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}
            footer={[
                <Button key="close" onClick={onClose}>
                    Закрыть
                </Button>,
            ]}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {!canEditTags && (
                    <Typography.Text type="secondary">
                        У вас нет прав на изменение тегов. Доступен только просмотр.
                    </Typography.Text>
                )}
                {/* Bulk actions */}
                {selectedRowKeys.length > 0 && (
                    <Space>
                        <Popconfirm
                            title={`Удалить ${selectedRowKeys.length} тегов?`}
                            description="Это действие нельзя отменить"
                            onConfirm={handleBulkDelete}
                            okText="Да"
                            cancelText="Нет"
                            disabled={!canEditTags}
                        >
                            <Button danger icon={<DeleteOutlined />} disabled={!canEditTags}>
                                Удалить выбранные ({selectedRowKeys.length})
                            </Button>
                        </Popconfirm>
                    </Space>
                )}

                {/* Create new tag */}
                {isCreating ? (
                    <Space direction="vertical" style={{ width: '100%', padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8, overflowX: 'hidden', boxSizing: 'border-box' }}>
                        <Input
                            placeholder="Название тега"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onPressEnter={handleCreateTag}
                            maxLength={10}
                            showCount
                        />
                        <div>
                            <div style={{ marginBottom: 8, fontWeight: 500 }}>Выберите цвет:</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: '100%' }}>
                                {COLOR_PALETTE.map((c) => (
                                    <div
                                        key={c}
                                        onClick={() => setNewColor(c)}
                                        style={{
                                            width: 28,
                                            height: 28,
                                            backgroundColor: c,
                                            cursor: 'pointer',
                                            border: newColor === c ? '2px solid #000' : '1px solid #d9d9d9',
                                            borderRadius: 4,
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                        <Space>
                            <Button type="primary" onClick={handleCreateTag}>
                                Создать
                            </Button>
                            <Button onClick={() => {
                                setIsCreating(false);
                                setNewName('');
                                setNewColor(COLOR_PALETTE[0]);
                            }}>
                                Отмена
                            </Button>
                        </Space>
                    </Space>
                ) : (
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            if (!canEditTags) {
                                message.error('Нет прав на изменение тегов');
                                return;
                            }
                            setIsCreating(true);
                        }}
                        block
                        style={{ width: '100%', minHeight: 36, whiteSpace: 'normal', lineHeight: '1.2' }}
                        disabled={!canEditTags}
                    >
                        Добавить новый тег
                    </Button>
                )}

                {/* Tags table */}
                <Table
                    rowSelection={canEditTags ? {
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                    } : undefined}
                    columns={columns}
                    dataSource={tagStore.tags}
                    rowKey="id"
                    pagination={false}
                    size="small"
                />
            </Space>
        </Modal>
    );
});

export default TagManagementModal;
export { COLOR_PALETTE };
